const MIGRATED_CLIENT_ID = "d8d58e22-937d-4215-971f-a521e790411c";
const MIGRATED_CLIENT_URL = "https://platform.qft-analytics.com/d8d58e22-937d-4215-971f-a521e790411c";

/**
 * Appends the client ID to a cookie named "access". The cookie is only valid
 * while the browser session is active.
 * @param clientId
 */
const setClientIdCookie = (clientId: string) => {
    const cookie = useCookie<string[]>("access", {
        default: () => [],
    });

    const currentValue = cookie.value as string[];
    if (!currentValue || currentValue.length === 0) {
        cookie.value = [clientId];
        // Save cookie (https://nuxt.com/docs/4.x/api/composables/use-cookie#writable-cookies)
        cookie.value &&= [...cookie.value];
        return;
    }

    if (!currentValue.includes(clientId)) {
        currentValue.push(clientId);
        cookie.value = currentValue;
        // Save cookie (https://nuxt.com/docs/4.x/api/composables/use-cookie#writable-cookies)
        cookie.value &&= [...cookie.value];
        return;
    }
};

const hasClientIdCookie = (clientId: string): boolean => {
    const cookie = useCookie("access", {
        default: () => [],
        readonly: true,
    });

    if (!cookie.value || (cookie.value as string[]).length === 0) {
        return false;
    }

    return (cookie.value as string[]).includes(clientId);
};

export default defineNuxtRouteMiddleware(async (to) => {
    // use admin middleware for /admin routes
    if (to.path.startsWith("/admin")) {
        return;
    }

    // Check if route contains a client ID
    const clientId = to.params.client_id as string | undefined;
    if (!clientId) {
        return;
    }

    if (clientId.toLowerCase() === MIGRATED_CLIENT_ID) {
        return navigateTo(MIGRATED_CLIENT_URL, { external: true });
    }

    const supabase = useSupabaseClient();

    // Use Supabase v2 auth helper to get the current user reliably
    const { data: userData, error: userError } = await supabase.auth.getUser();
    const user = userData?.user;

    if (!user || userError) {
        console.warn("User not authenticated, redirecting to login.", userError?.code);
        return navigateTo("/login");
    }

    if (hasClientIdCookie(clientId)) {
        return;
    }

    const { data, error } = await supabase
        .from("user_client")
        .select("id")
        .eq("client_id", clientId)
        .eq("user_id", user.id)
        .single();

    if (error || !data) {
        if (error) {
            console.error("Could not check client access:", {
                error: error,
                client_id: clientId,
                user_id: user.id,
            });
        } else {
            console.warn("No access record found for user and client, redirecting to home.");
        }
        return navigateTo("/");
    }

    setClientIdCookie(clientId);
});
